/*
 * Owns the cart's country-initialization lock. The layout marks a populated cart
 * inert when its saved country is missing or different, so stale lines cannot be
 * edited or checked out while the country check runs. The cart stays visible.
 * finish() releases the lock after the redraw, with a 15-second fail-safe; any
 * clear still running retains its own hbCartBusy hold until it settles.
 */
(() => {
  if (window.__hbCountryGate) return;

  const COUNTRY_GATE_FAILSAFE_MS = 15000;
  let resolveGate;
  const gate = {
    active: false,
    done: false,
    checkStarted: false,
    timer: null,
    promise: new Promise((resolve) => {
      resolveGate = resolve;
    }),
    finish(reason = "done") {
      if (gate.done) return;
      gate.done = true;
      clearTimeout(gate.timer);
      try {
        const container = document.getElementById("MainContentContainer");
        container?.classList.remove("cart-page-processing");
        container?.removeAttribute("inert");
      } catch (_) {}
      resolveGate(reason);
    },
  };

  try {
    const container = document.getElementById("MainContentContainer");
    gate.active = Boolean(container?.classList.contains("cart-page-processing"));
  } catch (_) {}

  if (gate.active) {
    gate.timer = setTimeout(() => {
      console.warn(`[CountryGate] Cart clear not finished after ${COUNTRY_GATE_FAILSAFE_MS} ms; releasing the initial lock.`);
      gate.finish("timeout");
    }, COUNTRY_GATE_FAILSAFE_MS);
  } else {
    gate.done = true;
    resolveGate("inactive");
  }

  window.__hbCountryGate = gate;
})();

/*
 * The market flow: the page-load country check and every cart clear (/cart/clear.js). window.__hbCountryGate
 * above only exists on the cart template, so the cart drawer (assets/hb-cart-drawer.js, every other page)
 * waits on this instead: window.hbWhenCartMarketSettled() resolves once every flow that is running has ended
 * (success, error or early return), or at once when none is. The page-load flow is registered as soon as this
 * script runs, before DOMContentLoaded starts the check, so a drawer opened on load (a login return) cannot
 * slip in ahead of a clear.
 */
const hbMarketFlows = new Set();

function hbBeginMarketFlow() {
  let settle;
  let ended = false;
  const flow = new Promise((resolve) => {
    settle = resolve;
  });
  hbMarketFlows.add(flow);
  // Idempotent: every path ends its flow, and a repeated end is ignored.
  return () => {
    if (ended) return;
    ended = true;
    hbMarketFlows.delete(flow);
    settle();
    // A clear empties the cart without a cartUpdate. The cart drawer (assets/hb-cart-drawer.js) listens for this to
    // drop anything it fetched before the flow ended, which describes the old market's bag.
    try {
      document.dispatchEvent(new CustomEvent("hb:cart:market-settled"));
    } catch {}
  };
}

window.hbWhenCartMarketSettled = async function hbWhenCartMarketSettled() {
  while (hbMarketFlows.size) {
    await Promise.all(Array.from(hbMarketFlows));
  }
};

const hbEndInitialMarketFlow = hbBeginMarketFlow();
// Nothing may wait on the page-load flow for ever: it normally ends inside the gate's 15 s fail-safe, and a
// script that runs after DOMContentLoaded never starts a check at all. The clears it covers are flows of
// their own, which this does not end.
if (document.readyState === "complete") {
  hbEndInitialMarketFlow();
} else {
  setTimeout(hbEndInitialMarketFlow, 20000);
}

/*
 * Checkout reprices the cart when the shopper changes the delivery country: the cart's country and currency
 * follow the address. A normal page load puts the cart back on the page's market, but Back can show a page
 * from the browser's cache (or bfcache) without asking the server. Every cart render after that (the drawer,
 * the cart page's back/forward refresh) then came back in the checkout country's prices on this market's
 * page. On those loads one GET of the cart icon section (sections/cart-icon-bubble.liquid prints the
 * country the server prices the cart in) checks the cart, and when it is not this page's market, the same
 * localization update the country selector posts moves it back. The lines are kept: only the market
 * changes, as a fresh load of this page would do. It runs inside a market flow, so the cart page and the
 * drawer wait for it (window.hbWhenCartMarketSettled) before they draw the cart.
 */
const HB_CART_MARKET_CHECK_TIMEOUT_MS = 3000;
const HB_CART_MARKET_UPDATE_TIMEOUT_MS = 5000;
const hbIsHistoryLoad = (() => {
  try {
    return performance.getEntriesByType("navigation")[0]?.type === "back_forward";
  } catch (_) {
    return false;
  }
})();

async function hbFetchWithTimeout(url, options, timeoutMs) {
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    return await fetch(url, { credentials: "same-origin", ...options, ...(controller ? { signal: controller.signal } : {}) });
  } finally {
    clearTimeout(timer);
  }
}

async function hbRealignCartMarket() {
  const pageCountry = (document.getElementById("HbCurrentCountryCode")?.value || "").trim().toUpperCase();
  if (!pageCountry) return;
  const root = window.Shopify?.routes?.root || "/";
  try {
    const response = await hbFetchWithTimeout(
      `${root}?section_id=cart-icon-bubble`,
      { cache: "no-store" },
      HB_CART_MARKET_CHECK_TIMEOUT_MS
    );
    if (!response.ok) return;
    const cartCountry = /data-hb-cart-country="([A-Z]{2})"/.exec(await response.text())?.[1];
    if (!cartCountry || cartCountry === pageCountry) return;
    const body = new URLSearchParams({
      form_type: "localization",
      utf8: "✓",
      _method: "put",
      return_to: `${location.pathname}${location.search}`,
      country_code: pageCountry,
      language_code: window.Shopify?.locale || document.documentElement.lang || "en",
    });
    // The answer is a redirect to return_to; following it would render a whole page nobody reads.
    await hbFetchWithTimeout(`${root}localization`, { method: "POST", body, redirect: "manual" }, HB_CART_MARKET_UPDATE_TIMEOUT_MS);
  } catch (e) {
    console.warn("[CountryGate] Could not check the cart's market after a history load (non-blocking):", e);
  }
}

// Registered before cart.js and hb-cart-drawer.js add theirs (this file loads first), so the flow exists
// when their pageshow handlers ask whether the market has settled.
window.addEventListener("pageshow", (event) => {
  if (!event.persisted) return;
  const endMarketFlow = hbBeginMarketFlow();
  hbRealignCartMarket().finally(endMarketFlow);
});

const countriesToStoreUrlMapping = {
  Albania: {
    url: "en-al",
    country_code: "AL",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Algeria: {
    url: "en-dz",
    country_code: "DZ",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Angola: {
    url: "en-ao",
    country_code: "AO",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Armenia: {
    url: "en-am",
    country_code: "AM",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Australia: {
    url: "en-au",
    country_code: "AU",
    currency_code: "AUD",
    currency_symbol: "$",
  },
  Austria: {
    url: "en-at",
    country_code: "AT",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Azerbaijan: {
    url: "en-az",
    country_code: "AZ",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Bahrain: {
    url: "en-bh",
    country_code: "BH",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Belgium: {
    url: "en-be",
    country_code: "BE",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Bermuda: {
    url: "en-bm",
    country_code: "BM",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Bolivia: {
    url: "en-bo",
    country_code: "BO",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Botswana: {
    url: "en-bw",
    country_code: "BW",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Brunei: {
    url: "en-bn",
    country_code: "BN",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Bulgaria: {
    url: "en-bg",
    country_code: "BG",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Cambodia: {
    url: "en-kh",
    country_code: "KH",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Canada: {
    url: "en-ca",
    country_code: "CA",
    currency_code: "CAD",
    currency_symbol: "$",
  },
  "Cayman Islands": {
    url: "en-ky",
    country_code: "KY",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Chile: {
    url: "en-cl",
    country_code: "CL",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Colombia: {
    url: "en-co",
    country_code: "CO",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Comoros: {
    url: "en-km",
    country_code: "KM",
    currency_code: "USD",
    currency_symbol: "$",
  },
  "Costa Rica": {
    url: "en-cr",
    country_code: "CR",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Croatia: {
    url: "en-hr",
    country_code: "HR",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Curaçao: {
    url: "en-cw",
    country_code: "CW",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Cyprus: {
    url: "en-cy",
    country_code: "CY",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Denmark: {
    url: "en-dk",
    country_code: "DK",
    currency_code: "USD",
    currency_symbol: "$",
  },
  "Dominican Republic": {
    url: "en-do",
    country_code: "DO",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Ecuador: {
    url: "en-ec",
    country_code: "EC",
    currency_code: "USD",
    currency_symbol: "$",
  },
  "El Salvador": {
    url: "en-sv",
    country_code: "SV",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Estonia: {
    url: "en-ee",
    country_code: "EE",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Ethiopia: {
    url: "en-et",
    country_code: "ET",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Fiji: {
    url: "en-fj",
    country_code: "FJ",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Finland: {
    url: "en-fi",
    country_code: "FI",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  France: {
    url: "en-fr",
    country_code: "FR",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Gambia: {
    url: "en-gm",
    country_code: "GM",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Germany: {
    url: "en-de",
    country_code: "DE",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Ghana: {
    url: "en-gh",
    country_code: "GH",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Greenland: {
    url: "en-gl",
    country_code: "GL",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Guadeloupe: {
    url: "en-gp",
    country_code: "GP",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Guatemala: {
    url: "en-gt",
    country_code: "GT",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Guyana: {
    url: "en-gt",
    country_code: "GY",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Honduras: {
    url: "en-hn",
    country_code: "HN",
    currency_code: "USD",
    currency_symbol: "$",
  },
  "Hong Kong": {
    url: "en-hk",
    country_code: "HK",
    currency_code: "USD",
    currency_symbol: "$",
    address_value_alias: "Hong Kong",
  },
  Hungary: {
    url: "en-hu",
    country_code: "HU",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Iceland: {
    url: "en-is",
    country_code: "IS",
    currency_code: "USD",
    currency_symbol: "$",
  },
  India: {
    url: "en-in",
    country_code: "IN",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Iraq: {
    url: "en-iq",
    country_code: "IQ",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Ireland: {
    url: "en-ie",
    country_code: "IE",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Italy: {
    url: "en-it",
    country_code: "IT",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Jamaica: {
    url: "en-jm",
    country_code: "JM",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Japan: {
    url: "en-jp",
    country_code: "JP",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Jordan: {
    url: "en-jo",
    country_code: "JO",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Kenya: {
    url: "en-ke",
    country_code: "KE",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Kuwait: {
    url: "en-kw",
    country_code: "KW",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Kyrgyzstan: {
    url: "en-kg",
    country_code: "KG",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Latvia: {
    url: "en-lv",
    country_code: "LV",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Luxembourg: {
    url: "en-lu",
    country_code: "LU",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Madagascar: {
    url: "en-mg",
    country_code: "MG",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Malawi: {
    url: "en-mw",
    country_code: "MW",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Maldives: {
    url: "en-mv",
    country_code: "MV",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Malta: {
    url: "en-mt",
    country_code: "MT",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Martinique: {
    url: "en-mq",
    country_code: "MQ",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Mauritania: {
    url: "en-mr",
    country_code: "MR",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Moldova: {
    url: "en-md",
    country_code: "MD",
    currency_code: "USD",
    currency_symbol: "$",
    address_value_alias: "Moldova, Republic of",
  },
  Montenegro: {
    url: "en-me",
    country_code: "ME",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Mozambique: {
    url: "en-mz",
    country_code: "MZ",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Nepal: {
    url: "en-np",
    country_code: "NP",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Netherlands: {
    url: "en-nl",
    country_code: "NL",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  "New Caledonia": {
    url: "en-nc",
    country_code: "NC",
    currency_code: "USD",
    currency_symbol: "$",
  },
  "New Zealand": {
    url: "en-nz",
    country_code: "NZ",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Nigeria: {
    url: "en-ng",
    country_code: "NG",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Norway: {
    url: "en-no",
    country_code: "NO",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Oman: {
    url: "en-om",
    country_code: "OM",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Panama: {
    url: "en-pa",
    country_code: "PA",
    currency_code: "USD",
    currency_symbol: "$",
  },
  "Papua New Guinea": {
    url: "en-pg",
    country_code: "PG",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Paraguay: {
    url: "en-py",
    country_code: "PY",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Philippines: {
    url: "en-ph",
    country_code: "PH",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Poland: {
    url: "en-pl",
    country_code: "PL",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Portugal: {
    url: "en-pt",
    country_code: "PT",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Qatar: {
    url: "en-qa",
    country_code: "QAT",
    currency_code: "QAR",
    currency_symbol: "ر.ق",
  },
  Romania: {
    url: "en-ro",
    country_code: "RO",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  "Saudi Arabia": {
    url: "en-sa",
    country_code: "SA",
    currency_code: "SAR",
    currency_symbol: "ر.س",
  },
  Singapore: {
    url: "en-sg",
    country_code: "SG",
    currency_code: "USD",
    currency_symbol: "$",
  },
  "Sint Maarten": {
    url: "en-sx",
    country_code: "SX",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Slovakia: {
    url: "en-sk",
    country_code: "SK",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Slovenia: {
    url: "en-si",
    country_code: "SI",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  "South Africa": {
    url: "en-za",
    country_code: "ZA",
    currency_code: "USD",
    currency_symbol: "$",
  },
  "South Korea": {
    url: "en-kr",
    country_code: "KR",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Spain: {
    url: "en-es",
    country_code: "ES",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  "Sri Lanka": {
    url: "en-lk",
    country_code: "LK",
    currency_code: "USD",
    currency_symbol: "$",
  },
  "St. Lucia": {
    url: "en-lc",
    country_code: "LC",
    currency_code: "USD",
    currency_symbol: "$",
    address_value_alias: "Saint Lucia",
  },
  Suriname: {
    url: "en-sr",
    country_code: "SR",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Sweden: {
    url: "en-se",
    country_code: "SE",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Switzerland: {
    url: "en-ch",
    country_code: "CH",
    currency_code: "EUR",
    currency_symbol: "€",
  },
  Taiwan: {
    url: "en-tw",
    country_code: "TW",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Tanzania: {
    url: "en-tz",
    country_code: "TZ",
    currency_code: "USD",
    currency_symbol: "$",
    address_value_alias: "Tanzania, United Republic Of",
  },
  "United Arab Emirates": {
    url: "en-ae",
    country_code: "AE",
    currency_code: "AED",
    currency_symbol: "د.إ",
  },
  "United Kingdom": {
    url: "en-gb",
    country_code: "GB",
    currency_code: "GBP",
    currency_symbol: "£",
  },
  "United States": {
    url: "en-us",
    country_code: "US",
    currency_code: "USD",
    currency_symbol: "$",
  },
  Vietnam: {
    url: "en-vn",
    country_code: "VN",
    currency_code: "USD",
    currency_symbol: "$",
  },
};

function renderCountries(countriesToStoreUrlMapping, currentCountryCode, countriesList) {
  countriesList.innerHTML = "";
  const countryListPrefix = countriesList.dataset.countryListPrefix || "country";
  const hiddenCountryCodes = new Set(
    (countriesList.dataset.hiddenCountryCodes || "")
      .split(",")
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean)
  );
  Object.entries(countriesToStoreUrlMapping).forEach(([name, data]) => {
    if (hiddenCountryCodes.has(data.country_code.toUpperCase())) return;

    const li = document.createElement("li");
    li.className = "disclosure__item";
    li.tabIndex = -1;

    const isCurrent = data.country_code === currentCountryCode;

    const el = document.createElement(isCurrent ? "span" : "a");
    el.className = "link link--text disclosure__link caption-large focus-inset";
    el.dataset.value = data.country_code;
    el.id = `${countryListPrefix}-${data.country_code.toLowerCase()}`;

    if (isCurrent) {
      el.setAttribute("aria-current", "true");
      el.setAttribute("aria-disabled", "true");
      el.classList.add("is-current");
    } else {
      el.href = window.location.origin + "/" + (data.url || "");
    }

    // Checkmark span (only visible for current country)
    const checkmarkSpan = document.createElement("span");
    if (!isCurrent) checkmarkSpan.className = "visibility-hidden";
    checkmarkSpan.innerHTML =
      '<svg class="icon-checkmark" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.7656 0.234375C11.4531 -0.078125 10.9375 -0.078125 10.6406 0.234375L3.75 7.10938L1.35938 4.73438C1.04688 4.42188 0.53125 4.42188 0.234375 4.73438C-0.078125 5.04688 -0.078125 5.5625 0.234375 5.85938L3.23438 8.85938C3.39062 9.01562 3.57812 9.09375 3.75 9.09375C3.92188 9.09375 4.125 9.01562 4.26562 8.85938L11.7656 1.35938C12.0781 1.04688 12.0781 0.53125 11.7656 0.234375Z" fill="currentColor"/></svg>';

    // Country name
    const countrySpan = document.createElement("span");
    countrySpan.className = "country";
    countrySpan.textContent = name;

    // Currency
    const currencySpan = document.createElement("span");
    currencySpan.className = "localization-form__currency motion-reduce";
    currencySpan.textContent = `(${data.currency_code} ${data.currency_symbol})`;

    el.appendChild(checkmarkSpan);
    el.appendChild(countrySpan);
    el.appendChild(currencySpan);
    li.appendChild(el);
    countriesList.appendChild(li);
  });
}

document.addEventListener("DOMContentLoaded", () => {
  // layout/theme.liquid renders #HbCurrentCountryCode on every page, whichever header selector design is
  // on. The classic selector's own [data-current-country-code] input is only a fallback: the combined
  // selector (snippets/country-language-selector.liquid) prints none.
  const currentCountryField =
    document.getElementById("HbCurrentCountryCode") || document.querySelector("[data-current-country-code]");
  const hiddenCountryCodes = new Set(
    (
      currentCountryField?.dataset.hiddenCountryCodes ||
      document.querySelector("[data-hidden-country-codes]")?.dataset.hiddenCountryCodes ||
      ""
    )
      .split(",")
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean)
  );
  const visibleCountriesToStoreUrlMapping = Object.fromEntries(
    Object.entries(countriesToStoreUrlMapping).filter(([, data]) => {
      return !hiddenCountryCodes.has(data.country_code.toUpperCase());
    })
  );

  window.countriesToStoreUrlMapping = visibleCountriesToStoreUrlMapping;
  // Blank only if neither field rendered. The market check then records and clears nothing (see
  // runCountryCheck): assuming US here turned every other market's page load into a country change.
  const currentCountryCode = (currentCountryField?.value || "").trim().toUpperCase();
  const countriesLists = document.getElementsByClassName("countries-selector");
  for (const countryList of countriesLists) {
    renderCountries(visibleCountriesToStoreUrlMapping, currentCountryCode, countryList);
  }

  const LS_KEY_LAST_COUNTRY = "hb:lastCountry";
  const LS_KEY_CLEAR_LOCK = "hb:cartClearLock";
  const LOCK_TTL_MS = 12000;
  // The page-load clear gives up after this, so it normally ends inside the gate's 15 s fail-safe.
  const CLEAR_TIMEOUT_MS = 8000;
  // Another tab's clear is one POST; waiting longer than this for it only keeps the veil up, for example
  // over a lock left behind by a page that reloaded mid-clear.
  const OTHER_TAB_CLEAR_WAIT_MS = 3000;
  // Bounds the one cart check that follows a clear this page cannot confirm (serverCartIsEmpty), so the
  // slowest path (8 s clear timeout, this, then the redraw) normally still ends inside the gate's 15 s
  // fail-safe. When it does not (the fail-safe timer starts before DOMContentLoaded), the veil lifts and
  // the market-clear-flow hold keeps checkout and the line controls locked until the redraw.
  const CART_CHECK_TIMEOUT_MS = 3000;
  // A clear this page cannot confirm leaves the market unrecorded, so the next page load clears again.
  // Once this many own clears in a row for one market end that way, the market is recorded anyway, as it
  // was before the retry existed: a shopper who is rate limited or on a slow network then stops paying a
  // clear, a cart read and (on the cart page) the veil on every page load. Reset whenever it is recorded.
  const LS_KEY_UNCONFIRMED_CLEARS = "hb:marketClearUnconfirmed";
  const MAX_UNCONFIRMED_CLEARS = 2;
  // Cart updates published when the shopper adds to the cart: product-form (PDP, quick add and the cart
  // page's sample and suggested-item cards), quick-add, pdp-cross-sell and the PDP's inline quantity control
  // (its + adds). Cart page line edits ("cart-items") and gift syncs are left out, since they only change
  // lines already in the cart.
  const SHOPPER_ADD_SOURCES = new Set(["product-form", "quick-add", "pdp-cross-sell", "pdp-inline-quantity", "account-buy-again"]);

  function acquireLock() {
    try {
      const now = Date.now();
      const raw = localStorage.getItem(LS_KEY_CLEAR_LOCK);
      if (raw) {
        const { ts } = JSON.parse(raw);
        if (now - ts < LOCK_TTL_MS) return false;
      }
      localStorage.setItem(LS_KEY_CLEAR_LOCK, JSON.stringify({ ts: now }));
      return true;
    } catch (_) {
      return true;
    }
  }
  function releaseLock() {
    try {
      localStorage.removeItem(LS_KEY_CLEAR_LOCK);
    } catch (_) {}
  }
  // Every write of hb:lastCountry goes through here, so the unconfirmed-clear count never outlives it.
  function recordCountry() {
    try {
      localStorage.setItem(LS_KEY_LAST_COUNTRY, currentCountryCode);
    } catch (_) {}
    try {
      localStorage.removeItem(LS_KEY_UNCONFIRMED_CLEARS);
    } catch (_) {}
  }

  // The cart keeps its token through /cart/clear.js, so cart.js's promo badge flag (keyed on that token)
  // would otherwise make the next cart page in this tab reserve a badge row for codes the clear removed.
  // Dropped only once the clear is known to have run: a refused or timed-out clear can leave an applied
  // code in the cart, whose row the flag still has to reserve.
  function forgetCartPromoBadges() {
    try {
      sessionStorage.removeItem("hb-cart-promo-badges");
    } catch (_) {}
  }

  // /cart/clear.js empties the lines but keeps the cart's attributes. The saved hazmat volume
  // (_prevHazmatVolume, assets/hb-hazmat-volume.js) describes the old market's lines, and left behind it blocked
  // hazmat items and samples in the emptied bag. It is reset with the lines, in the same market flow, and only
  // when one was saved (hb-hazmat-volume.js re-reads the bag after this flow). Never throws: the clear itself has
  // landed.
  async function resetClearedCartHazmatVolume(clearResponse) {
    try {
      const cleared = await clearResponse.json().catch(() => null);
      if (!(Number(cleared?.attributes?._prevHazmatVolume) > 0)) return;
      await fetch("/cart/update.js?dk_silent=1", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-DK-Silent": "1" },
        body: JSON.stringify({ attributes: { _prevHazmatVolume: "" } }),
        keepalive: true,
      });
    } catch (e) {
      console.warn("[CountryGate] Hazmat volume reset after clear failed (non-blocking):", e);
    }
  }

  // Resolves "ok" when the server answered the clear with a 2xx, "failed" when it answered otherwise
  // (429/5xx answers resolve fetch without clearing anything) or the request failed, and "unknown" when
  // this page cannot see the outcome: timeoutMs aborted a request that may still land, or window.clearCart
  // ran it (no theme file defines one today, and it returns no status). Only the page-load clear passes
  // timeoutMs and reads the result; window.beforeCountryChange waits for its clear as before.
  async function clearCartFast(timeoutMs = 0) {
    const endMarketFlow = hbBeginMarketFlow();
    const controller = timeoutMs && typeof AbortController === "function" ? new AbortController() : null;
    const abortTimer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      if (typeof window.clearCart === "function") {
        await window.clearCart({ timeoutMs: 8000, silent: true });
        // It reports no status, so this path keeps dropping the flag as it always has.
        forgetCartPromoBadges();
        return "unknown";
      }
      const response = await fetch("/cart/clear.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        keepalive: true,
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (response.ok) {
        forgetCartPromoBadges();
        await resetClearedCartHazmatVolume(response);
        return "ok";
      }
      console.warn(`[CountryGate] Cart clear answered HTTP ${response.status} (non-blocking).`);
      return "failed";
    } catch (e) {
      console.warn("[CountryGate] clearCart failed (non-blocking):", e);
      return controller?.signal.aborted ? "unknown" : "failed";
    } finally {
      clearTimeout(abortTimer);
      endMarketFlow();
    }
  }

  // One read of the server cart after a clear this page cannot confirm: its own clear timed out or was
  // refused, or another tab ran it. True only when /cart.js answers with an empty cart; lines left, an
  // error or the timeout give false, so the country is not recorded and the next page load clears again
  // within the limits in runCountryCheck (the clear is idempotent, and an emptied cart then skips it
  // through renderedCartIsKnownEmpty).
  async function serverCartIsEmpty() {
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const abortTimer = controller ? setTimeout(() => controller.abort(), CART_CHECK_TIMEOUT_MS) : null;
    try {
      const response = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
        cache: "no-store",
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (!response.ok) return false;
      const cart = await response.json();
      return Number(cart?.item_count) === 0;
    } catch (_) {
      return false;
    } finally {
      clearTimeout(abortTimer);
    }
  }

  // Counts one more own clear for this market that ended unconfirmed and says whether that reaches
  // MAX_UNCONFIRMED_CLEARS. A count left for another market starts again from one.
  function unconfirmedClearLimitReached() {
    let count = 1;
    try {
      const last = JSON.parse(localStorage.getItem(LS_KEY_UNCONFIRMED_CLEARS));
      if (last?.country === currentCountryCode) count += Math.max(0, Number(last.count) || 0);
    } catch (_) {}
    try {
      localStorage.setItem(LS_KEY_UNCONFIRMED_CLEARS, JSON.stringify({ country: currentCountryCode, count }));
    } catch (_) {}
    return count >= MAX_UNCONFIRMED_CLEARS;
  }

  // Watches this page for the shopper's first add from the moment its clear (or its wait for another
  // tab's clear) starts. While the market is not recorded the next page load clears again, and that retry
  // must not take away what the shopper has added in this market since (before the retry existed, those
  // lines were kept). So an add records the market: at once when the check has already ended unrecorded
  // (armed), otherwise when it ends. Any old lines then stay, as they did before.
  function watchShopperAdds() {
    const watch = { added: false, armed: false, stop() {} };
    try {
      if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return watch;
      if (!PUB_SUB_EVENTS.cartUpdate) return watch;
      const unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!SHOPPER_ADD_SOURCES.has(event?.source)) return;
        watch.added = true;
        if (!watch.armed) return;
        watch.stop();
        recordCountry();
      });
      watch.stop = () => {
        watch.armed = false;
        try {
          unsubscribe();
        } catch (_) {}
      };
    } catch (_) {}
    return watch;
  }

  // True only for a fresh network render of an empty cart. The check is window.hbRenderedCartIsKnownEmpty in
  // assets/cart-essentials.js (deferred like this file, so it has run by DOMContentLoaded), shared with the
  // bubble refresh there. With its switch HB_CART_ON_FIRST_ADD_ONLY on (the default), no cart cookie is needed:
  // a cookie-less clear would only create an empty cart (every page after it then renders uncached), so a
  // first visitor gets no cart until their first add, which is what Signifyd's purchase-intent release in
  // layout/theme.liquid waits for. With the switch off it also needs a cart cookie, and the clear below creates
  // a first visitor's cart on page 1 as before.
  // If the helper is missing or throws, the cart is not known empty and the clear runs as it did before.
  function renderedCartIsKnownEmpty() {
    try {
      return typeof window.hbRenderedCartIsKnownEmpty === "function" && window.hbRenderedCartIsKnownEmpty() === true;
    } catch (_) {
      return false;
    }
  }

  // The same check, never with the cookie test (window.hbRenderedCartWasEmpty, assets/cart-essentials.js): the
  // server drew this page, its header bubble included, with an empty bag. Only used to skip the bubble refresh
  // after a clear; whether the clear runs is decided by renderedCartIsKnownEmpty above.
  function renderedCartWasEmpty() {
    try {
      return typeof window.hbRenderedCartWasEmpty === "function" && window.hbRenderedCartWasEmpty() === true;
    } catch (_) {
      return false;
    }
  }

  // Resolves once another tab's clear (hb:cartClearLock) is released or has gone stale, bounded by
  // OTHER_TAB_CLEAR_WAIT_MS, so this tab normally lifts its veil onto the cleared cart instead of its
  // stale lines. A lock nobody releases (its page unloaded mid-clear) costs this wait, not the lock's TTL.
  // Either way this tab does not learn how that clear ended, so the caller checks the cart afterwards.
  async function waitForClearLockRelease() {
    const deadline = Date.now() + OTHER_TAB_CLEAR_WAIT_MS;
    while (Date.now() < deadline) {
      try {
        const raw = localStorage.getItem(LS_KEY_CLEAR_LOCK);
        if (!raw) return;
        const { ts } = JSON.parse(raw);
        if (!(Date.now() - ts < LOCK_TTL_MS)) return;
      } catch (_) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  }

  // The gate's fail-safe lifts the veil, and cart.js's "market-clear" hold with it, even when the clear
  // below is still running. This second hold keeps checkout and the line controls locked until the cart
  // is drawn after the clear. The waits are capped (8 s clear timeout, 3 s for another tab), so after a
  // timeout the hold lasts until one check of the server cart and a redraw from it: a clear that lands
  // later than that can still reach a page the shopper can act on, and the country is then normally left
  // unrecorded (see runCountryCheck), so the next page load settles it. Under the veil it changes nothing.
  // Without a gate (other pages, or the veil was never added) there is no hold.
  function holdCartForMarketClear() {
    if (!window.__hbCountryGate?.active) return null;
    try {
      return window.hbCartBusy?.acquire?.("market-clear-flow") || null;
    } catch (_) {
      return null;
    }
  }

  // Redraws the cart page (or, on any other page, the cart drawer if it has been drawn) from the server after
  // a clear. updateCartPage (assets/cart.js) leaves the view as it is when the redraw is refused (a 429, a
  // 5xx or an answer with no cart) and resolves true only when the redraw landed, so a skip is told by its
  // answer (an older cart.js that answers nothing: by <main> not having been replaced). When the server has
  // confirmed the cart empty (cartIsEmpty), a skipped redraw would lift the veil over the old market's lines
  // and prices, and a tap on one would then act on a line that is gone. The page is switched to its empty
  // layout instead, as cart.js does when a removal leaves nothing (is-empty on <cart-items> and the footer
  // hides the lines, the totals and checkout). That is safer than keeping the busy hold and the market
  // unrecorded, which would leave those stale lines on screen, locked, until the shopper reloads.
  // The milestone progress bar stays visible in the empty layout and still shows the old market's amounts
  // and currency; the new market's thresholds are only known to the server, so it is emptied, and the
  // next page load draws it for the new market. The free-shipping banner (layout/theme.liquid) sits outside
  // <main>, where no redraw reaches it, and the server draws none for an empty cart, so a confirmed empty
  // cart empties it whether or not the redraw landed.
  async function redrawCartAfterClear(cartIsEmpty) {
    const cartItems = document.querySelector("cart-items");
    if (!cartItems || typeof cartItems.updateCartPage !== "function") return;
    const pageBefore = document.querySelector(".content-for-layout");
    const redrawn = await cartItems.updateCartPage();
    if (!cartIsEmpty) return;
    const emptySection = (id) => {
      const section = document.getElementById(id);
      if (!section) return;
      section.classList.add("is-empty");
      section.replaceChildren();
    };
    try {
      emptySection("cart-page-free-shipping-banner");
    } catch (_) {}
    // updateCartPage says whether the redraw landed (on the cart page and in the cart drawer alike); an older
    // cart.js that answers nothing is told by <main> having been replaced.
    const redrawLanded =
      typeof redrawn === "boolean" ? redrawn : document.querySelector(".content-for-layout") !== pageBefore;
    if (redrawLanded) return;
    try {
      document.querySelector("cart-items.cart-page")?.classList.add("is-empty");
      document.getElementById("main-cart-footer")?.classList.add("is-empty");
      document.querySelectorAll("[data-cart-count]").forEach((count) => {
        count.textContent = "(0)";
      });
      emptySection("cart-page-milestone-progress");
    } catch (_) {}
  }

  // Lifts the market-change veil however the check ends. The clear paths below lift it as soon as the
  // cart is redrawn after the clear; this finally covers the paths that clear nothing, early returns and
  // errors.
  async function ensureCountryInitialized() {
    const gate = window.__hbCountryGate;
    if (gate) gate.checkStarted = true;
    try {
      await runCountryCheck();
      if (hbIsHistoryLoad) await hbRealignCartMarket();
    } finally {
      gate?.finish?.();
      hbEndInitialMarketFlow();
    }
  }

  async function runCountryCheck() {
    if (!currentCountryCode) return;

    let saved = null;
    try {
      saved = localStorage.getItem(LS_KEY_LAST_COUNTRY);
    } catch (_) {}

    const mustClear = !saved || saved !== currentCountryCode;

    // A market already recorded needs no clear. An empty cart (or none yet) has nothing from another
    // market to clear either: skip /cart/clear.js, the cart page refresh and the cart/update.js bubble
    // refresh (first visits, or when localStorage does not persist), but still record the country.
    // window.beforeCountryChange (selector and welcome popup) still clears an existing cart; with no cart
    // yet it skips the clear too.
    if (!mustClear || renderedCartIsKnownEmpty()) {
      recordCountry();
      return;
    }

    // Past this point, the page records hb:lastCountry only once the cart is known to hold nothing from
    // another market: after a clear the server confirmed, or a server cart read as empty. Otherwise the
    // next page load clears again instead of leaving the old market's lines in place, within two limits:
    // the shopper's first add on this page records the market (watchShopperAdds), and so does the
    // MAX_UNCONFIRMED_CLEARS-th own clear in a row that ends unconfirmed. A page without the veil that
    // finds another tab's lock learns nothing about the cart, so it leaves the market unrecorded: the
    // lock holder records it when its clear succeeds, and a cart it emptied lets the next page load record
    // it without a clear (renderedCartIsKnownEmpty).
    let cartSettled = false;
    let ownClearUnconfirmed = false;
    const addWatch = watchShopperAdds();
    try {
      if (acquireLock()) {
        const flowHold = holdCartForMarketClear();
        let clearStatus;
        try {
          try {
            clearStatus = await clearCartFast(CLEAR_TIMEOUT_MS);
          } finally {
            releaseLock();
          }
          // A refused clear, or one aborted after its request went out, may or may not have emptied the
          // cart. Read it once first, so the redraw below is at least as fresh as that read.
          cartSettled = clearStatus === "ok" || (await serverCartIsEmpty());
          ownClearUnconfirmed = !cartSettled;
          if (cartSettled) forgetCartPromoBadges();
          await redrawCartAfterClear(cartSettled);
          window.__hbCountryGate?.finish?.();
        } finally {
          window.hbCartBusy?.release?.(flowHold);
        }
        // The header bubble was drawn by the server with this page. When that was a fresh network render of an
        // empty bag and the server confirmed the clear, the clear cannot have changed the bubble, so its refresh
        // (a POST cart/update.js plus a section render) is skipped, and assets/cart-essentials.js skips its own
        // too while the cart stays unchanged. That case only reaches here with HB_CART_ON_FIRST_ADD_ONLY off (a
        // first visit, whose clear creates the cart); a clear that failed or timed out keeps the refresh.
        if (typeof window.updateCartUI === "function" && !(clearStatus === "ok" && renderedCartWasEmpty())) {
          await window.updateCartUI(false);
        }
      } else if (window.__hbCountryGate?.active) {
        // Another tab holds the clear lock and is clearing this same cart. The cart page used to be
        // uncovered here only by cart.js's first-load release, over lines that were being removed. This
        // tab cannot see how that clear ends (the lock is released either way, and the wait gives up at
        // 3 s), so it reads the server cart once before the redraw and records the market only if it is
        // empty; that tab records it too when its clear succeeds. Leaving mid-wait therefore records
        // nothing here. Redrawing costs one cart page render request, the read one /cart.js request.
        const flowHold = holdCartForMarketClear();
        try {
          await waitForClearLockRelease();
          cartSettled = await serverCartIsEmpty();
          await redrawCartAfterClear(cartSettled);
          window.__hbCountryGate.finish();
        } finally {
          window.hbCartBusy?.release?.(flowHold);
        }
      }
    } finally {
      // Only this page's own clears count towards the limit: another tab counts its own.
      if (cartSettled || addWatch.added || (ownClearUnconfirmed && unconfirmedClearLimitReached())) {
        addWatch.stop();
        recordCountry();
      } else {
        addWatch.armed = true;
      }
    }
  }

  window.beforeCountryChange = async () => {
    // No cart yet (assets/cart-essentials.js, HB_CART_ON_FIRST_ADD_ONLY): nothing to clear, and a cookie-less
    // POST /cart/clear.js would create an empty cart on the way to the new market.
    if (typeof window.hbNoCartYet === "function" && window.hbNoCartYet()) return;
    const endMarketFlow = hbBeginMarketFlow();
    try {
      if (acquireLock()) {
        try {
          await clearCartFast();
        } finally {
          releaseLock();
        }
      }
    } finally {
      endMarketFlow();
    }
  };

  ensureCountryInitialized();
});

// Runs after the listener above even when it threw before the market check started (for example while
// rendering the country lists), so the veil is not left up for the whole fail-safe.
document.addEventListener("DOMContentLoaded", () => {
  const gate = window.__hbCountryGate;
  if (gate && !gate.checkStarted) gate.finish("not-started");
  // The market check never started (the listener above threw first), so no flow is left to wait for.
  if (!gate?.checkStarted) hbEndInitialMarketFlow();
});
